import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    CardBody,
    Button,
    Row,
    Col,
    Spinner,
    FormGroup,
    Label,
    Input,
    Progress,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from "reactstrap";
import { SketchPicker } from 'react-color';
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { setTheme, updatePrimaryColor } from "../../../slices/theme/reducer";
// Import Firebase storage
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid'; // For generating unique file names
import { useCurrentUser } from "../../../slices/auth/hooks";
import { saveTheme } from "../../../slices/theme/thunk";
import { DASHBOARD_USER_ROLES } from "../../../constants";
import CopyInviteLink from "../../../Components/Buttons/CopyInviteLinkButton";
import { createIndividualReferralLinkFromPromoCode, getBaseUrl } from "../../../utils/utils";
import { generatePresignedUrl } from "../../../slices/upload/thunk";

const CustomTheme = ({
    userTheme,
    loading,
    onRefresh,
    canModify = true
}) => {
    const dispatch = useDispatch();
    const globalTheme = useSelector(state => state.Theme);
    const currentUser = useCurrentUser();

    const userRole = currentUser.role;

    const isNational = userRole === DASHBOARD_USER_ROLES.NATIONAL;
    const isGA = userRole === DASHBOARD_USER_ROLES.GA;
    const isBroker = userRole === DASHBOARD_USER_ROLES.BROKER;

    const showAccessLinks = isNational || isGA || isBroker;


    const [isLoading, setIsLoading] = useState(loading);
    const [localTheme, setLocalTheme] = useState({
        primaryColor: globalTheme.primaryColor || "#3E79F7",
        secondaryColor: globalTheme.secondaryColor || "#212529",
        logo: globalTheme.logo,
        logoPath: globalTheme.logoPath,
        allowOverride: globalTheme.allowOverride,
        appName: globalTheme.appName
    });

    console.log("Local theme:", localTheme);
    console.log("Global theme:", globalTheme);

    // State for new uploads (separate from saved state)
    const [newLogoFile, setNewLogoFile] = useState(null);
    const [newLogo, setNewLogo] = useState(null);
    const [newLogoPath, setNewLogoPath] = useState(null);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [allowOverride, setAllowOverride] = useState(localTheme.allowOverride || false);

    const [showPrimaryColorPicker, setShowPrimaryColorPicker] = useState(false);
    const [showSecondaryColorPicker, setShowSecondaryColorPicker] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [activeLogoTab, setActiveLogoTab] = useState('primary');

    const logoInputRef = useRef(null);

    // Initialize theme from props when component mounts or userTheme changes
    useEffect(() => {
        if (globalTheme) {
            setLocalTheme({
                primaryColor: globalTheme.primaryColor || "#3E79F7",
                secondaryColor: globalTheme.secondaryColor || "#212529",
                logo: globalTheme.logo || null,
                logoPath: globalTheme.logoPath || null,
                allowOverride: globalTheme.allowOverride || false,
                appName: globalTheme.appName || ""
            });

            // Reset any new uploads
            setNewLogoFile(null);
            setNewLogo(null);
            setNewLogoPath(null);
            setHasUnsavedChanges(false);

            // Update global theme
            dispatch(setTheme({
                primaryColor: globalTheme.primaryColor,
                secondaryColor: globalTheme.secondaryColor,
                logo: globalTheme.logo || null,
                logoPath: globalTheme.logoPath || null,
                allowOverride: globalTheme.allowOverride || false,
                appName: globalTheme.appName || ""
            }));
        }
        setIsLoading(loading);
    }, [globalTheme, loading, dispatch]);

    const handlePrimaryColorChange = (color) => {
        setLocalTheme({
            ...localTheme,
            primaryColor: color.hex,
        });
        setHasUnsavedChanges(true);

        // Update global theme for live preview
        // dispatch(updatePrimaryColor(color.hex));
    };

    const handleSecondaryColorChange = (color) => {
        setLocalTheme({
            ...localTheme,
            secondaryColor: color.hex,
        });
        setHasUnsavedChanges(true);

        // You would need to add a similar method in your reducer to update secondary color
        // Or update the whole theme at once
        // dispatch(setTheme({
        //     ...localTheme,
        //     secondaryColor: color.hex
        // }));
    };

    const handleLogoChange = async (e, logoType = 'primary') => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Check file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                Swal.fire({
                    title: "Error!",
                    text: "Logo file must be less than 2MB",
                    icon: "error",
                    confirmButtonText: "OK",
                });
                return;
            }

            // Check file type
            if (!file.type.match('image/(jpeg|jpg|png|svg+xml)')) {
                Swal.fire({
                    title: "Error!",
                    text: "Logo must be an image file (JPEG, PNG, or SVG)",
                    icon: "error",
                    confirmButtonText: "OK",
                });
                return;
            }

            try {
                // Start the upload process
                await uploadLogoToS3WithPresignedUrl(file, logoType);
            } catch (error) {
                console.error(`Error uploading ${logoType} logo:`, error);
                Swal.fire({
                    title: "Error!",
                    text: `Failed to upload ${logoType} logo. Please try again.`,
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    // Add this toggle handler
    const handleAllowOverrideToggle = () => {
        setAllowOverride(!allowOverride);
        setLocalTheme({
            ...localTheme,
            allowOverride: !allowOverride,
        });
        setHasUnsavedChanges(true);
    };

    const uploadLogoToFirebase = async (file, logoType = 'primary') => {
        if (!file) {
            console.error("No file provided to upload!");
            return;
        }
        if (!(file instanceof Blob)) {
            console.error("Provided file is not a Blob/File!", file);
            return;
        }

        console.log('File details:', file);
        console.log('File type:', file.type);
        console.log('File size:', file.size);
        console.log('Logo type:', logoType);

        setIsUploading(true);
        setUploadProgress(0);

        try {
            console.log("Starting upload process...");
            const storage = getStorage();
            console.log("Storage instance:", storage);

            const fileExtension = file.name.split('.').pop();
            const fileName = `public/${currentUser.id}/logos/${logoType}_${uuidv4()}.${fileExtension}`;
            console.log("File will be saved as:", fileName);

            const storageRef = ref(storage, fileName);

            const uploadTask = uploadBytesResumable(storageRef, file);

            console.log("Upload task created, attaching listeners...");

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`Upload progress: ${progress.toFixed(2)}%`);
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Upload failed:", error);
                    setIsUploading(false);
                    Swal.fire({
                        title: "Upload Error!",
                        text: `Failed to upload: ${error.message}`,
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                },
                async () => {
                    console.log("Upload completed successfully!");
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        console.log("Download URL:", downloadURL);

                        // Store new logo details separately from the saved theme
                        if (logoType === 'primary') {
                            setNewLogoFile(file);
                            setNewLogo(downloadURL);
                            setNewLogoPath(fileName);
                        }

                        setHasUnsavedChanges(true);
                        setIsUploading(false);

                        Swal.fire({
                            title: "Success!",
                            text: `${logoType === 'primary' ? 'Primary' : 'White'} logo uploaded successfully. Don't forget to save your changes!`,
                            icon: "success",
                            confirmButtonText: "OK",
                        });
                    } catch (urlError) {
                        console.error("Error getting download URL:", urlError);
                        setIsUploading(false);
                        Swal.fire({
                            title: "Error!",
                            text: "File uploaded but couldn't get download URL",
                            icon: "error",
                            confirmButtonText: "OK",
                        });
                    }
                }
            );
        } catch (criticalError) {
            console.error("Critical error in uploadLogoToFirebase:", criticalError);
            setIsUploading(false);
            Swal.fire({
                title: "Critical Error!",
                text: "Failed to start upload process",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    const uploadLogoToS3WithPresignedUrl = async (file, logoType = 'primary') => {
        if (!file) {
            console.error("No file provided to upload!");
            return;
        }
        if (!(file instanceof Blob)) {
            console.error("Provided file is not a Blob/File!", file);
            return;
        }

        console.log('File details:', file);
        console.log('File type:', file.type);
        console.log('File size:', file.size);
        console.log('Logo type:', logoType);

        setIsUploading(true);
        setUploadProgress(0);

        try {
            console.log("Starting upload process...");

            const fileExtension = file.name.split('.').pop();
            const fileName = `public/${currentUser.id}/logos/${logoType}_${uuidv4()}.${fileExtension}`;
            console.log("File will be saved as:", fileName);

            // First, get a presigned URL from your backend

            const payload = {
                fileName: fileName,
                fileType: file.type,
                fileSize: file.size
            }

            const presignedUrlResponse = await dispatch(generatePresignedUrl(payload)).unwrap();

            if (!presignedUrlResponse || !presignedUrlResponse.uploadUrl) {
                throw new Error("Failed to get presigned URL");
            }

            const { uploadUrl, publicUrl } = presignedUrlResponse;


            // Upload file directly to S3 using presigned URL
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentage = Math.round((event.loaded / event.total) * 100);
                    console.log(`Upload progress: ${percentage}%`);
                    setUploadProgress(percentage);
                }
            });

            await new Promise((resolve, reject) => {
                xhr.addEventListener('load', () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        console.log("Upload completed successfully!");
                        resolve();
                    } else {
                        reject(new Error(`Upload failed with status: ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', (err) => {
                    console.log("Upload error:", err);
                    reject(new Error('Network error during upload'));
                });

                xhr.open('PUT', uploadUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
            });

            // Store new logo details
            if (logoType === 'primary') {
                setNewLogoFile(file);
                setNewLogo(publicUrl);
                setNewLogoPath(fileName);
            }

            setHasUnsavedChanges(true);
            setIsUploading(false);

            Swal.fire({
                title: "Success!",
                text: `${logoType === 'primary' ? 'Primary' : 'White'} logo uploaded successfully. Don't forget to save your changes!`,
                icon: "success",
                confirmButtonText: "OK",
            });

        } catch (error) {
            console.error("Upload failed:", error);
            setIsUploading(false);
            Swal.fire({
                title: "Upload Error!",
                text: `Failed to upload: ${error.message}`,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    const handleCancelChanges = () => {
        // Discard all changes and revert to the saved theme

        // Reset logos
        setNewLogoFile(null);
        setNewLogo(null);
        setNewLogoPath(null);

        // Reset colors and allowOverride
        setAllowOverride(globalTheme.allowOverride || false); // Reset this too

        // Reset colors
        dispatch(setTheme({
            primaryColor: globalTheme.primaryColor,
            secondaryColor: globalTheme.secondaryColor,
            logo: globalTheme.logo,
            logoPath: globalTheme.logoPath,
            allowOverride: globalTheme.allowOverride,
            appName: globalTheme.appName
        }));

        setLocalTheme({
            primaryColor: globalTheme.primaryColor,
            secondaryColor: globalTheme.secondaryColor,
            logo: globalTheme.logo,
            logoPath: globalTheme.logoPath,
            allowOverride: globalTheme.allowOverride,
            appName: globalTheme.appName
        });

        // Reset file inputs
        if (logoInputRef.current) {
            logoInputRef.current.value = "";
        }


        setHasUnsavedChanges(false);
    };

    const handleSaveTheme = async () => {
        try {
            setIsLoading(true);

            // Prepare updated theme
            const updatedTheme = {
                ...localTheme
            };

            // Update with new logos if available
            if (newLogo) {
                updatedTheme.logo = newLogo;
                updatedTheme.logoPath = newLogoPath;
            }

            console.log("Updated theme before saving:", updatedTheme);

            // Save the theme to backend
            const saveResponse = await dispatch(saveTheme({
                primaryColor: updatedTheme.primaryColor,
                secondaryColor: updatedTheme.secondaryColor,
                logo: updatedTheme.logo,
                logoPath: updatedTheme.logoPath,
                allowOverride: updatedTheme.allowOverride,
                ownerId: currentUser.id,
                ownerType: currentUser.role,
                appName: updatedTheme.appName,
                id: userTheme ? userTheme.id : null
            }));

            console.log("Save response:", saveResponse);

            if (saveResponse.error) {
                throw new Error("Error saving theme");
            }

            // Update local state to match what's now saved
            setLocalTheme({
                primaryColor: updatedTheme.primaryColor,
                secondaryColor: updatedTheme.secondaryColor,
                logo: updatedTheme.logo,
                logoPath: updatedTheme.logoPath,
                allowOverride: updatedTheme.allowOverride,
                appName: updatedTheme.appName
            });

            // Clear the new logo states since they're now saved
            setNewLogoFile(null);
            setNewLogo(null);
            setNewLogoPath(null);
            setHasUnsavedChanges(false);

            // Update global theme
            dispatch(setTheme({
                primaryColor: updatedTheme.primaryColor,
                secondaryColor: updatedTheme.secondaryColor,
                logo: updatedTheme.logo,
                logoPath: updatedTheme.logoPath,
                allowOverride: updatedTheme.allowOverride,
                appName: updatedTheme.appName
            }));

            if (onRefresh)
                onRefresh();

            Swal.fire({
                title: "Success!",
                text: "Theme saved successfully",
                icon: "success",
                confirmButtonText: "OK",
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Error!",
                text: "Error saving theme",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleLogoTab = (tab) => {
        if (activeLogoTab !== tab) {
            setActiveLogoTab(tab);
        }
    };

    if (isLoading) {
        return (
            <Card className="p-3 mt-4 shadow-none">
                <div className="d-flex align-items-center justify-content-between">
                    <h4>Custom Theme</h4>
                </div>
                <hr />
                <CardBody>
                    <div className="d-flex justify-content-center align-items-center">
                        <Spinner color="primary" />
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="p-3 mt-4 shadow-none">
            <div className="d-flex align-items-center justify-content-between">
                <h4>Custom Theme</h4>
                {canModify && (
                    <div className="d-flex justify-content-end">
                        {hasUnsavedChanges && (
                            <Button
                                onClick={handleCancelChanges}
                                className="btn btn-soft-primary me-2"
                                disabled={isLoading || isUploading}
                            >
                                Cancel Changes
                            </Button>
                        )}
                        <Button
                            onClick={handleSaveTheme}
                            className="btn btn-primary"
                            disabled={isLoading || isUploading || !hasUnsavedChanges}
                        >
                            {isLoading ? <Spinner size="sm" /> : "Save Theme"}
                        </Button>
                    </div>
                )}
            </div>
            <hr />
            <CardBody>
                {/* Name and Logo on the same row */}
                <Row className="mb-4">
                    <Col md={6}>
                        <FormGroup>
                            <Label for="themeName">Name</Label>
                            <Input
                                id="themeName"
                                type="text"
                                value={localTheme.appName}
                                onChange={(e) => {
                                    setLocalTheme({ ...localTheme, appName: e.target.value });
                                    setHasUnsavedChanges(true);
                                }}
                                disabled={!canModify}
                            />
                            <small className="text-muted">
                                This name represents your brand. Default is "BaxRx".
                            </small>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Logo</Label>
                            {canModify && !isUploading && (
                                <Input
                                    type="file"
                                    id="normalLogo"
                                    onChange={(e) => handleLogoChange(e)}
                                    innerRef={logoInputRef}
                                    accept="image/jpeg, image/png, image/svg+xml"
                                    disabled={isUploading}
                                />
                            )}

                            {isUploading && (
                                <div className="mt-2">
                                    <p className="mb-1">Uploading logo... {Math.round(uploadProgress)}%</p>
                                    <Progress value={uploadProgress} />
                                </div>
                            )}

                            {/* Show the new logo if one is selected but not saved yet */}
                            {newLogo && !isUploading && (
                                <div className="mt-2 p-2 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
                                    <small className="text-muted d-block mb-1">New Logo (Not Saved)</small>
                                    <img
                                        src={newLogo}
                                        alt="New Logo Preview"
                                        style={{
                                            maxWidth: '120px',
                                            maxHeight: '40px',
                                        }}
                                    />
                                </div>
                            )}

                            {/* Show current saved logo if it exists and no new logo is selected */}
                            {localTheme.logo && !newLogo && !isUploading && (
                                <div className="mt-2">
                                    <small className="text-muted d-block mb-1">Current Logo</small>
                                    <div className="bg-dark p-2 rounded d-inline-block">
                                        <img
                                            src={localTheme.logo}
                                            alt="Current Logo"
                                            style={{
                                                maxWidth: '120px',
                                                maxHeight: '40px',
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            <small className="text-muted d-block mt-1">
                                Recommended: 200x60px. Max: 2MB.
                            </small>
                        </FormGroup>
                    </Col>
                </Row>

                {/* Colors Row */}
                <Row className="mb-4">
                    <Col md={12}>
                        <h6 className="mb-3">Colors</h6>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label for="primaryColor">Primary Color</Label>
                                    <div>
                                        <div
                                            className="color-preview"
                                            onClick={() => canModify && setShowPrimaryColorPicker(!showPrimaryColorPicker)}
                                            style={{
                                                backgroundColor: localTheme.primaryColor,
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '4px',
                                                cursor: canModify ? 'pointer' : 'default',
                                                border: '1px solid #ced4da',
                                            }}
                                        />
                                        {showPrimaryColorPicker && canModify && (
                                            <div className="color-picker-popover">
                                                <div
                                                    className="color-picker-cover"
                                                    onClick={() => setShowPrimaryColorPicker(false)}
                                                    style={{
                                                        position: 'fixed',
                                                        top: '0px',
                                                        right: '0px',
                                                        bottom: '0px',
                                                        left: '0px',
                                                    }}
                                                />
                                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                                    <SketchPicker
                                                        color={localTheme.primaryColor}
                                                        onChange={handlePrimaryColorChange}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <small className="text-muted">
                                        For buttons, links, and UI elements.
                                    </small>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label for="secondaryColor">Secondary Color</Label>
                                    <div>
                                        <div
                                            className="color-preview"
                                            onClick={() => canModify && setShowSecondaryColorPicker(!showSecondaryColorPicker)}
                                            style={{
                                                backgroundColor: localTheme.secondaryColor,
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '4px',
                                                cursor: canModify ? 'pointer' : 'default',
                                                border: '1px solid #ced4da',
                                            }}
                                        />
                                        {showSecondaryColorPicker && canModify && (
                                            <div className="color-picker-popover">
                                                <div
                                                    className="color-picker-cover"
                                                    onClick={() => setShowSecondaryColorPicker(false)}
                                                    style={{
                                                        position: 'fixed',
                                                        top: '0px',
                                                        right: '0px',
                                                        bottom: '0px',
                                                        left: '0px',
                                                    }}
                                                />
                                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                                    <SketchPicker
                                                        color={localTheme.secondaryColor}
                                                        onChange={handleSecondaryColorChange}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <small className="text-muted">
                                        For sidebar and secondary elements.
                                    </small>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Toggle Section */}
                {(isNational || isGA) && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <FormGroup>
                                <div className="d-flex align-items-center">
                                    <div className="form-check form-switch me-3">
                                        <Input
                                            type="switch"
                                            id="allowOverrideToggle"
                                            checked={allowOverride}
                                            onChange={handleAllowOverrideToggle}
                                            disabled={!canModify || isLoading || isUploading}
                                            className="form-switch-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label className="mb-0">
                                            {isNational ? "Allow GAs to Customize Theme" : "Allow Brokers to Customize Theme"}
                                        </Label>
                                        <small className="text-muted d-block">
                                            {isNational
                                                ? "Enable this to allow your GA's to create their own custom theme"
                                                : "Enable this to allow your Brokers to create their own custom theme"}
                                        </small>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default CustomTheme;